import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { normaliseProject, compactPrice } from '../config/projectSeo';
import Reveal from './Reveal';

/**
 * District-grouped index of project links.
 *
 * The homepage already fetches 100 projects for the map but only ever linked
 * to 10 of them. Every other project URL existed solely in sitemap.xml — and a
 * URL that a sitemap declares but nothing links to is exactly what Search
 * Console reports as "Discovered — currently not indexed": Google knows it
 * exists and sees no reason to spend crawl budget on it.
 *
 * This component spends the links the homepage already had available, grouped
 * by district so the block is genuinely useful to a visitor too, and capped so
 * that no single page sprays hundreds of equal-weight links.
 */

const MAX_DISTRICTS = 10;
const MAX_PER_DISTRICT = 8;

export default function InternalLinkHub({ projects = [] }) {
    const { i18n } = useTranslation();
    const lang = (i18n.language || 'ar').split('-')[0] === 'ar' ? 'ar' : 'en';
    const isRtl = lang === 'ar';

    const districts = useMemo(() => {
        const byDistrict = new Map();
        for (const raw of projects) {
            const p = normaliseProject(raw);
            if (!p.id || !p.title || !p.district) continue;
            if (!byDistrict.has(p.district)) byDistrict.set(p.district, []);
            byDistrict.get(p.district).push(p);
        }
        return [...byDistrict.entries()]
            // Busiest districts first — they carry the queries with volume.
            .sort((a, b) => b[1].length - a[1].length)
            .slice(0, MAX_DISTRICTS)
            .map(([name, items]) => ({
                name,
                total: items.length,
                items: items
                    // Cheapest first: "from AED X" is the query people type.
                    .sort((a, b) => (a.priceMin || Infinity) - (b.priceMin || Infinity))
                    .slice(0, MAX_PER_DISTRICT)
            }));
    }, [projects]);

    if (districts.length === 0) return null;

    return (
        <section
            className="container link-hub"
            dir={isRtl ? 'rtl' : 'ltr'}
        >
            <style>{`
                .link-hub { padding: 3rem 1rem 5rem; }

                .link-hub-head { max-width: 680px; margin-bottom: 2.5rem; }

                .link-hub-title {
                    font-size: clamp(1.75rem, 4vw, 2.5rem);
                    color: #fff;
                    font-weight: 800;
                    margin-bottom: 0.75rem;
                }

                .link-hub-rule {
                    height: 4px;
                    width: 100px;
                    background: var(--color-gold);
                    border-radius: 2px;
                    margin-bottom: 1.25rem;
                }

                .link-hub-lede {
                    color: #9a9aa8;
                    line-height: 1.8;
                    margin: 0;
                    font-size: 0.98rem;
                }

                /* One card per district. The wall of 80 equal-weight links was
                   the whole problem — a bordered panel per district turns it
                   into ten small, scannable lists. */
                .link-hub-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(310px, 1fr));
                    gap: 1.25rem;
                }

                /* Same frosted pane as the property cards, so the two
                   sections do not read as two different materials. */
                .link-hub-card {
                    background: linear-gradient(150deg,
                        rgba(255, 255, 255, 0.08) 0%,
                        rgba(255, 255, 255, 0.028) 44%,
                        rgba(255, 255, 255, 0.05) 100%);
                    backdrop-filter: blur(24px) saturate(1.7);
                    -webkit-backdrop-filter: blur(24px) saturate(1.7);
                    border: 1px solid rgba(255, 255, 255, 0.09);
                    border-radius: var(--r-xl);
                    padding: 1.35rem 1.4rem 1.15rem;
                    transition: border-color 0.3s ease, background 0.3s ease,
                                transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
                }

                .link-hub-card:hover {
                    border-color: rgba(255, 215, 0, 0.34);
                    background: linear-gradient(150deg,
                        rgba(255, 255, 255, 0.12) 0%,
                        rgba(255, 255, 255, 0.045) 44%,
                        rgba(255, 236, 170, 0.06) 100%);
                    transform: translateY(-3px);
                }

                .link-hub-card-head {
                    display: flex;
                    align-items: baseline;
                    justify-content: space-between;
                    gap: 0.75rem;
                    padding-bottom: 0.8rem;
                    margin-bottom: 0.5rem;
                    border-bottom: 1px solid rgba(255, 215, 0, 0.16);
                }

                .link-hub-district {
                    font-size: 1.08rem;
                    font-weight: 700;
                    color: var(--color-gold);
                    margin: 0;
                }

                /* Says how big the district is at a glance — the old markup
                   only revealed the count in a link at the very bottom. */
                .link-hub-count {
                    flex-shrink: 0;
                    font-size: 0.72rem;
                    font-weight: 600;
                    color: rgba(255, 255, 255, 0.55);
                    background: rgba(255, 255, 255, 0.06);
                    border-radius: var(--r-pill);
                    padding: 3px 9px;
                    white-space: nowrap;
                }

                .link-hub-list {
                    list-style: none;
                    margin: 0;
                    padding: 0;
                }

                .link-hub-row {
                    display: flex;
                    align-items: baseline;
                    justify-content: space-between;
                    gap: 0.9rem;
                    padding: 0.55rem 0.5rem;
                    margin-inline: -0.5rem;
                    border-radius: var(--r-sm);
                    text-decoration: none;
                    transition: background 0.2s ease, color 0.2s ease;
                }

                .link-hub-row + .link-hub-row { margin-top: 1px; }

                .link-hub-row:hover { background: rgba(255, 215, 0, 0.07); }

                /* Was #bbb for every row. Near-white for the name, gold only
                   for the price, so the eye has something to sort by. */
                .link-hub-name {
                    color: #d6d6e0;
                    font-size: 0.93rem;
                    line-height: 1.45;
                    transition: color 0.2s ease;
                }

                .link-hub-row:hover .link-hub-name { color: #fff; }

                .link-hub-price {
                    color: var(--color-gold);
                    font-size: 0.85rem;
                    font-weight: 600;
                    white-space: nowrap;
                    flex-shrink: 0;
                }

                .link-hub-price small {
                    color: rgba(255, 255, 255, 0.4);
                    font-size: 0.74em;
                    font-weight: 500;
                    margin-inline-end: 3px;
                }

                .link-hub-all {
                    display: inline-block;
                    margin-top: 0.9rem;
                    color: var(--color-neon-blue);
                    font-size: 0.85rem;
                    font-weight: 600;
                    text-decoration: none;
                }

                .link-hub-all:hover { text-decoration: underline; }

                @media (max-width: 480px) {
                    .link-hub-grid { gap: 1rem; }
                    .link-hub-card { padding: 1.1rem 1.15rem 1rem; }
                }
            `}</style>

            <div className="link-hub-head">
                <h2 className="link-hub-title">
                    {isRtl ? 'مشاريع دبي على الخارطة حسب المنطقة' : 'Dubai Off-Plan Projects by District'}
                </h2>
                <div className="link-hub-rule" />
                <p className="link-hub-lede">
                    {isRtl
                        ? 'تصفّح المشاريع المتاحة حالياً في أبرز مناطق دبي، مرتّبة من الأقل سعراً. جميع الأسعار من المطوّر مباشرة وبدون عمولة على المشتري.'
                        : 'Browse the projects currently available across the main Dubai districts, lowest price first. All prices are direct from the developer with zero buyer commission.'}
                </p>
            </div>

            <div className="link-hub-grid">
                {districts.map((d, i) => (
                    <Reveal
                        key={d.name}
                        className="link-hub-card"
                        /* Stagger across the row, then reset, so a wide grid
                           does not end with a card waiting a full second. */
                        delay={(i % 3) * 0.09}
                    >
                        <div className="link-hub-card-head">
                            <h3 className="link-hub-district">{d.name}</h3>
                            <span className="link-hub-count">
                                {isRtl ? `${d.total} مشروع` : `${d.total} project${d.total === 1 ? '' : 's'}`}
                            </span>
                        </div>

                        <ul className="link-hub-list">
                            {d.items.map((p) => (
                                <li key={p.id}>
                                    <Link to={`/project/${p.id}`} className="link-hub-row">
                                        <span className="link-hub-name">{p.title}</span>
                                        {p.priceMin > 0 && (
                                            <span className="link-hub-price">
                                                <small>{isRtl ? 'من' : 'from'}</small>
                                                {compactPrice(p.priceMin, lang)}
                                            </span>
                                        )}
                                    </Link>
                                </li>
                            ))}
                        </ul>

                        {d.total > MAX_PER_DISTRICT && (
                            <Link
                                to={`/projects?district=${encodeURIComponent(d.name)}`}
                                className="link-hub-all"
                            >
                                {isRtl
                                    ? `كل مشاريع ${d.name} (${d.total}) \u2190`
                                    : `All ${d.name} projects (${d.total}) \u2192`}
                            </Link>
                        )}
                    </Reveal>
                ))}
            </div>
        </section>
    );
}
